import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authApi } from '../api/authApi';
import { onSessionCleared, onEmailVerificationRequired, tokenStore, refreshTokenStore, authSessionStore } from '../api/client';
import { disconnect as disconnectRealtime } from '../services/realtime';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [emailLocked, setEmailLocked] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me?.user || me || null);
      setEmailLocked(false);
      return me;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!tokenStore.get()) {
      setBootstrapping(false);
      return;
    }
    refreshMe().finally(() => setBootstrapping(false));
  }, [refreshMe]);

  useEffect(() => onSessionCleared(() => setUser(null)), []);
  useEffect(() => onEmailVerificationRequired(() => setEmailLocked(true)), []);

  // Login accepts an optional 2FA payload on the second attempt
  // ({ twoFactorCode } or { recoveryCode }). The first call
  // without those throws an ApiError with code === 'TWO_FACTOR_REQUIRED'.
  const login = useCallback(async (email, password, twoFactor) => {
    const data = await authApi.login(email, password, twoFactor);
    setUser(data?.user || null);
    setEmailLocked(false);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await authApi.register(payload);
    setUser(data?.user || null);
    setEmailLocked(false);
    return data;
  }, []);

  const logout = useCallback(async () => {
    const rt = refreshTokenStore.get();
    try { await authApi.logout(rt); } catch {}
    authSessionStore.clear();
    disconnectRealtime();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, bootstrapping, emailLocked, refreshMe, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
